import {Checkbox, FormControl, Radio, TextField} from "@material-ui/core";
import Autocomplete, {
  AutocompleteCloseReason,
  AutocompleteRenderOptionState,
} from "@material-ui/lab/Autocomplete";
import React, {ReactNode, useContext, useMemo, useState} from "react";
import {isEqual} from "lodash";
import {Controller, RegisterOptions, UseFormReturn} from "react-hook-form";
import {FormContext} from "view/components/Form";
import useStyles from "view/components/Form/styles";
import {MultiSelectOption} from "view/components/Form/inputs/BasicSelect";

export enum AutocompleteOptionType {
  Checkbox = "checkbox",
  Radio = "radio",
}

type AutoCompleteSelectProps = {
  label: string;
  name: string;
  customId?: string;
  options: MultiSelectOption[];
  required?: boolean;
  rules?: RegisterOptions;
  selectStyle?: Record<string, any>;
  multiple?: boolean;
  handleChange?: (
    filter: MultiSelectOption | MultiSelectOption[] | null
  ) => void;
  optionType?: AutocompleteOptionType;
  searchPlaceholder?: string;
};

export type AutocompleteOption = {
  value: string;
  label: ReactNode;
  parentValue?: string;
};
const AutoCompleteSelect: React.FC<AutoCompleteSelectProps> = ({
  label,
  name,
  options,
  customId,
  required = true,
  rules,
  multiple = false,
  selectStyle,
  handleChange,
  optionType = AutocompleteOptionType.Checkbox,
  searchPlaceholder,
}) => {
  const classes = useStyles();
  const form = useContext(FormContext) as UseFormReturn<any>;
  const [open, setOpen] = useState<boolean>(false);

  const selectedValue = form.watch(name);
  const transformedOptions = useMemo<AutocompleteOption[]>(
    () =>
      options.flatMap((opt) => {
        const allOptions: AutocompleteOption[] = [
          {value: opt.value as string, label: opt.label},
        ];
        if (opt.subOptions?.length) {
          for (const subOpt of opt.subOptions) {
            allOptions.push({
              value: subOpt.value as string,
              label: subOpt.label,
              parentValue: opt.value as string,
            });
          }
        }

        return allOptions;
      }),
    [options]
  );

  const renderOption = (
    option: AutocompleteOption,
    state: AutocompleteRenderOptionState
  ) => {
    if (optionType === AutocompleteOptionType.Radio) {
      const isSubOption = !!option.parentValue;

      const isSelected = !isSubOption
        ? selectedValue?.value === option.value ||
          selectedValue?.parentValue === option.value
        : selectedValue?.value === option.value;

      return (
        <span>
          <Radio
            color="primary"
            checked={isSelected}
            style={{paddingLeft: option.parentValue ? 48 : 8}}
          />
          {option.label}
        </span>
      );
    }
    return (
      <span>
        <Checkbox
          color="primary"
          size="small"
          style={{marginRight: 8}}
          checked={state.selected}
        />
        {option.label}
      </span>
    );
  };

  const handleOpen = () => setOpen(true);
  const handleClose = (
    _: React.ChangeEvent<Record<string, unknown>>,
    closeReason: AutocompleteCloseReason
  ) => {
    if (["remove-option", "select-option"].includes(closeReason)) {
      return;
    }
    setOpen(false);
  };
  return (
    <Controller
      control={form.control}
      name={name}
      rules={{
        required,
        ...rules,
      }}
      render={({field, fieldState}) => {
        const {onChange, value, name, ref} = field;

        const {invalid} = fieldState;
        return (
          <FormControl
            style={{width: "100%"}}
            variant="outlined"
            error={invalid}
          >
            <Autocomplete
              ref={ref}
              multiple={multiple && optionType !== AutocompleteOptionType.Radio}
              classes={{paper: classes.autocompletePaper}}
              fullWidth
              open={open}
              getOptionSelected={(
                option: MultiSelectOption,
                value: MultiSelectOption
              ) => isEqual(option, value)}
              onOpen={handleOpen}
              onClose={handleClose}
              id={customId || name}
              options={transformedOptions}
              style={selectStyle}
              className={classes.input}
              value={value}
              getOptionLabel={(option) => {
                const defaultLabel = option.label as string;
                if (optionType !== AutocompleteOptionType.Radio) {
                  return defaultLabel;
                }

                if (!option.parentValue) {
                  return defaultLabel;
                }
                const parentOption = transformedOptions.find(
                  (op) => op.value === option.parentValue
                );
                if (!parentOption) {
                  return defaultLabel;
                }

                return `${parentOption.label} - ${option.label}`;
              }}
              onChange={(_, newValues) => {
                handleChange ? handleChange(newValues) : onChange(newValues);
              }}
              renderOption={renderOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={label}
                  placeholder={searchPlaceholder}
                />
              )}
              filterOptions={
                optionType === AutocompleteOptionType.Radio
                  ? (opts, state) => {
                      const input = state.inputValue.toLowerCase().trim();

                      const searchFiltered = opts.filter((option) =>
                        (option.label as string).toLowerCase().includes(input)
                      );
                      const matchedParentValues = new Set(
                        searchFiltered.reduce<string[]>((acc, curr) => {
                          if (curr.parentValue) {
                            acc.push(curr.parentValue);
                          }
                          return acc;
                        }, [])
                      );

                      return opts.filter((option) => {
                        if (!option.parentValue) {
                          return (
                            searchFiltered.some(
                              (o) => o.value === option.value
                            ) || matchedParentValues.has(option.value)
                          );
                        }

                        return searchFiltered.some(
                          (o) => o.value === option.value
                        );
                      });
                    }
                  : undefined
              }
            />
          </FormControl>
        );
      }}
    />
  );
};

export default AutoCompleteSelect;
